// FILE: src/components/CoachAvatar.js
//
// CoachAvatar — the single, presentational entry point for rendering the
// PM Pixel mascot anywhere in the app.  It knows nothing about exams,
// learners, or analytics; callers just ask for a pose:
//
//     <CoachAvatar pose="thumbsup" />
//     <CoachAvatar pose="certified" size="lg" />
//     <CoachAvatar pose="contemplative" />   // portrait-class (see registry)
//
// PHASE 1 (now): pose -> file resolution lives in the internal POSE_REGISTRY
// below, pointing at /themes/pm-pixel/mascot/.  The component's *public API*
// is already theme-shaped, so Phase 2 swaps the registry/base-path for values
// read from ThemeContext (useTheme()) with NO change to any call site.
//
// Two presentation classes are supported intentionally (not an accident):
//   • 'full'     — full-body coaching poses, 1:2 (192x384 asset, shown 96x192).
//                  Celebration, coaching, encouragement, dashboard moments.
//   • 'portrait' — tighter framing for reflection / interpretation / nuanced
//                  moments.  Used sparingly.  `contemplative` is the first
//                  candidate and is currently UNDER REVIEW (not wired to any
//                  interaction point yet).
//
// No Redux.  Functional component, hooks only.

import React from 'react';
import mcss from './CoachAvatar.module.css';

// ─── Phase-1 theme stand-in ──────────────────────────────────────────────────
// In Phase 2 these two values come from the active theme manifest
// (theme.mascot.avatarBasePath + theme.mascot.poseMappings).  Keeping them here
// as constants for now lets us ship the mascot without the theme layer.
const AVATAR_BASE_PATH = '/themes/pm-pixel/mascot/';

// Default pose used when an unknown / unmapped token is requested.
const FALLBACK_POSE = 'idle';

// pose token -> descriptor.  `frame` drives sizing; intrinsic w/h preserve the
// asset's true aspect ratio so we never stretch a sprite.  `alt` is the
// human-readable label used when the avatar is NOT decorative.
const POSE_REGISTRY = {
  idle: {
    file: 'PM_PIXEL_IDLE.png',
    frame: 'full',
    w: 192, h: 384,
    alt: 'PM Pixel standing',
  },
  thumbsup: {
    file: 'PM_PIXEL_THUMBSUP.png',
    frame: 'full',
    w: 192, h: 384,
    alt: 'PM Pixel giving a thumbs up',
  },
  cheer: {
    file: 'PM_PIXEL_CHEER.png',
    frame: 'full',
    w: 192, h: 384,
    alt: 'PM Pixel cheering',
  },
  notepad: {
    file: 'PM_PIXEL_NOTEPAD.png',
    frame: 'full',
    w: 192, h: 384,
    alt: 'PM Pixel taking notes',
  },
  certified: {
    file: 'PM_PIXEL_CERTIFIED.png',
    frame: 'full',
    w: 192, h: 384,
    alt: 'PM Pixel holding a certificate',
  },
  study: {
    file: 'PM_PIXEL_STUDY.png',
    frame: 'full',
    // Native 2:3 sprite (192x288) — a slightly shorter full-body framing than
    // the 1:2 poses. Width is derived from this ratio so nothing is stretched.
    w: 192, h: 288,
    alt: 'PM Pixel studying',
  },

  // ── Portrait class — UNDER REVIEW, not wired to any moment in Phase 1 ──
  // contemplative ships as a tighter close-up rather than a full-body pose.
  // Its production framing (currently 384x384) is being evaluated as a
  // deliberate "portrait / emphasis" class for reflection moments.  It is
  // intentionally a different aspect ratio from the full-body poses.
  contemplative: {
    file: 'PM_PIXEL_CONTEMPLATIVE.png',
    frame: 'portrait',
    w: 384, h: 384,
    alt: 'PM Pixel thinking',
  },

  // Break-time pose — used by the ExamBreak overlay during Simulation
  // scheduled breaks. Same square portrait class as contemplative.
  break: {
    file: 'PM_PIXEL_BREAK.png',
    frame: 'portrait',
    w: 384, h: 384,
    alt: 'PM Pixel taking a coffee break',
  },
};

// Displayed HEIGHT (px) per size token.  Width is derived from the pose's
// intrinsic aspect ratio so nothing is stretched.  'md' for a full-body pose
// resolves to the canonical 96x192 display size.
const SIZE_HEIGHT = {
  sm: 96,
  md: 192,
  lg: 288,
};

/**
 * @param {string}  pose        Required. One of the POSE_REGISTRY tokens
 *                              (idle | thumbsup | cheer | notepad | certified |
 *                              contemplative).  Unknown tokens fall back to idle.
 * @param {string}  [size]      'sm' | 'md' | 'lg'  (default 'md').
 * @param {number}  [heightPx]  Optional explicit display height in px. Overrides
 *                              `size` for one-off placements (e.g. the landing
 *                              hero) that need a non-standard scale. Width is
 *                              still derived from the pose's aspect ratio.
 * @param {boolean} [decorative] When true (default), the image is aria-hidden
 *                              with empty alt — it's ornamental.  Set false to
 *                              expose the pose's alt text to assistive tech.
 * @param {string}  [loading]   <img> loading attr. Defaults to 'lazy'; pass
 *                              'eager' for above-the-fold use (e.g. landing hero).
 * @param {string}  [className] Optional extra class on the wrapper.
 */
export default function CoachAvatar({
  pose,
  size = 'md',
  heightPx,
  decorative = true,
  loading = 'lazy',
  className = '',
}) {
  // If the requested image 404s or fails to decode, hide it gracefully —
  // never show a broken-image icon.  (Generalises the one-off
  // `characterImgFailed` pattern that lived in ExamSelector.)
  const [failed, setFailed] = React.useState(false);

  // ── Mascot enabled? (Phase 2: theme.mascot.enabled) ──
  // White-label themes can disable the mascot entirely; this component then
  // renders nothing app-wide with no per-call-site changes.
  const mascotEnabled = true;
  if (!mascotEnabled) return null;

  // ── Resolve pose with fallback ──
  let token = pose;
  let descriptor = POSE_REGISTRY[token];
  if (!descriptor) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[CoachAvatar] Unknown pose "${pose}". Falling back to "${FALLBACK_POSE}".`
      );
    }
    token = FALLBACK_POSE;
    descriptor = POSE_REGISTRY[FALLBACK_POSE];
  }

  // Defense in depth: if even the fallback is missing, or the image failed to
  // load, render nothing rather than a broken box.
  if (!descriptor || failed) return null;

  const height = heightPx || SIZE_HEIGHT[size] || SIZE_HEIGHT.md;
  const width = Math.round(height * (descriptor.w / descriptor.h));

  const frameClass = descriptor.frame === 'portrait' ? mcss.portrait : mcss.full;
  const wrapperClass = [mcss.avatar, frameClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={wrapperClass}
      style={{ width, height }}
      data-testid="coach-avatar"
      data-pose={token}
      data-frame={descriptor.frame}
    >
      <img
        className={mcss.img}
        src={AVATAR_BASE_PATH + descriptor.file}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        alt={decorative ? '' : descriptor.alt}
        aria-hidden={decorative ? 'true' : undefined}
        draggable="false"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
